import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatMoney } from "./offer-customer-lines";
import { includedLine, lineAmount, newClientKey, numberValue, parseOfferPaste } from "./utils/offer-workspace";
import { reorderCalcItems } from "./utils/offer-calc-item-groups";
import { fetchUnits } from "../../../api/unit";
import "./offer-workspace.css";

const NUMERIC_FIELDS = new Set(["quantity", "sales_price", "discount", "purchase_price", "surcharge"]);

export default function OfferQuickLines({ items, onChange, currency = "NOK" }) {
  const { t } = useTranslation();
  const [advanced, setAdvanced] = useState(false);
  const [units, setUnits] = useState([]);
  const [density, setDensity] = useState("normal");
  const [paste, setPaste] = useState("");
  const [pasteError, setPasteError] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkDiscount, setBulkDiscount] = useState(0);

  useEffect(() => {
    let active = true;
    fetchUnits({ per_page: 200 })
      .then((result) => {
        if (active) setUnits(Array.isArray(result) ? result : result?.data || []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const key = (row) => row.client_key || row.id;

  const update = (index, patch) => {
    const nextPatch = { ...patch };
    Object.keys(nextPatch).forEach((field) => {
      if (NUMERIC_FIELDS.has(field) && nextPatch[field] !== "" && nextPatch[field] != null) {
        nextPatch[field] = numberValue(nextPatch[field]);
      }
    });
    const target = { ...items[index], ...nextPatch };
    onChange(
      items.map((row, i) => {
        if (i === index) return target;
        if (
          target.alternative_group?.trim() &&
          target.pricing_role === "included" &&
          row.alternative_group === target.alternative_group
        ) {
          return { ...row, pricing_role: "optional" };
        }
        return row;
      })
    );
  };

  const add = (rows) =>
    onChange([
      ...items,
      ...rows.map((row, index) => ({
        ...row,
        client_key: row.client_key || newClientKey(),
        row_order: items.length + index,
      })),
    ]);

  const removeRow = (index) => {
    const row = items[index];
    if (!row?.id) {
      onChange(items.filter((_, i) => i !== index));
      return;
    }
    onChange(items.map((item, i) => (i === index ? { ...item, delete: true } : item)));
  };

  const moveRow = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    onChange(reorderCalcItems(items, fromIndex, toIndex));
  };

  const fields = [
    "title",
    "quantity",
    "sales_price",
    "discount",
    ...(advanced ? ["purchase_price", "alternative_group", "price_source", "price_dated_at"] : []),
  ];

  const visibleItems = items
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => !row.delete);

  return (
    <div className={`offer-quick-lines ${density === "compact" ? "is-compact" : ""}`}>
      <p className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        {t("offers.tabs.legacy_edit_banner")}
      </p>
      <div className="offer-workspace-tools">
        <button
          type="button"
          className="offer-workspace-primary"
          onClick={() =>
            add([
              {
                title: "",
                quantity: 1,
                sales_price: 0,
                discount: 0,
                surcharge: 0,
                item_type: "manual",
                visible_to_customer: true,
                pricing_role: "included",
              },
            ])
          }
        >
          + {t("offers.workspace.add_line")}
        </button>
        <button type="button" aria-pressed={advanced} onClick={() => setAdvanced(!advanced)}>
          {t("offers.workspace.advanced")}
        </button>
        <div
          className="offer-density-toggle"
          role="group"
          aria-label={t("offers.workspace.density")}
        >
          {["normal", "compact"].map((mode) => (
            <button
              key={mode}
              type="button"
              className={density === mode ? "is-active" : ""}
              aria-pressed={density === mode}
              onClick={() => setDensity(mode)}
            >
              {t(`offers.workspace.${mode}`)}
            </button>
          ))}
        </div>
        <details>
          <summary>{t("offers.workspace.paste")}</summary>
          <div className="offer-workspace-popover">
            <label>
              {t("offers.workspace.paste_hint")}
              <textarea value={paste} onChange={(e) => setPaste(e.target.value)} rows={5} />
            </label>
            {pasteError ? <p role="alert">{t("offers.workspace.paste_error")}</p> : null}
            <button
              type="button"
              onClick={() => {
                try {
                  add(parseOfferPaste(paste));
                  setPaste("");
                  setPasteError(false);
                } catch {
                  setPasteError(true);
                }
              }}
            >
              {t("offers.workspace.add_line")}
            </button>
          </div>
        </details>
        {selected.length > 0 ? (
          <label>
            {t("offers.workspace.bulk_discount")}{" "}
            <input
              type="number"
              min="0"
              max="100"
              value={bulkDiscount}
              onChange={(e) => setBulkDiscount(e.target.value)}
            />
            <button
              type="button"
              onClick={() =>
                onChange(
                  items.map((row) =>
                    selected.includes(key(row))
                      ? { ...row, discount: Math.min(100, Math.max(0, numberValue(bulkDiscount))) }
                      : row
                  )
                )
              }
            >
              {t("offers.workspace.apply")}
            </button>
          </label>
        ) : null}
      </div>

      <div className="offer-quick-scroll">
        <table>
          <thead>
            <tr>
              <th aria-label={t("offers.workspace.select")} />
              {fields.map((field) => (
                <th key={field}>{t(`offers.workspace.${field}`)}</th>
              ))}
              <th>{t("offers.workspace.unit")}</th>
              <th>{t("offers.workspace.inclusion")}</th>
              <th>{t("offers.workspace.total")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visibleItems.map(({ row, index }) => (
              <tr
                key={key(row) || index}
                className={[
                  row.pricing_role === "excluded" ? "is-excluded" : "",
                  "is-draggable",
                ]
                  .filter(Boolean)
                  .join(" ")}
                draggable
                onDragStart={(e) => {
                  const payload = JSON.stringify({ index });
                  e.dataTransfer.setData("application/x-promang-quick-line", payload);
                  e.dataTransfer.setData("text/plain", payload);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  e.currentTarget.classList.add("is-drop-target");
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove("is-drop-target");
                }}
                onDrop={(e) => {
                  e.currentTarget.classList.remove("is-drop-target");
                  e.preventDefault();
                  const raw =
                    e.dataTransfer.getData("application/x-promang-quick-line") ||
                    e.dataTransfer.getData("text/plain");
                  if (!raw) return;
                  try {
                    const { index: fromIndex } = JSON.parse(raw);
                    if (fromIndex == null) return;
                    moveRow(fromIndex, index);
                  } catch {
                    /* ignore */
                  }
                }}
              >
                <td>
                  <input
                    type="checkbox"
                    aria-label={t("offers.workspace.select")}
                    checked={selected.includes(key(row))}
                    onChange={(e) =>
                      setSelected(
                        e.target.checked
                          ? [...selected, key(row)]
                          : selected.filter((id) => id !== key(row))
                      )
                    }
                  />
                </td>
                {fields.map((field) => (
                  <td key={field}>
                    <input
                      aria-label={`${t(`offers.workspace.${field}`)} ${index + 1}`}
                      type={
                        field === "price_dated_at"
                          ? "date"
                          : ["title", "alternative_group", "price_source"].includes(field)
                            ? "text"
                            : "number"
                      }
                      step="any"
                      min={field === "discount" ? 0 : undefined}
                      max={field === "discount" ? 100 : undefined}
                      value={row[field] ?? ""}
                      onChange={(e) => update(index, { [field]: e.target.value })}
                    />
                  </td>
                ))}
                <td>
                  <select
                    className="offer-field-select"
                    aria-label={t("offers.workspace.unit")}
                    value={row.unit_id || ""}
                    onChange={(e) =>
                      update(index, {
                        unit_id: e.target.value || null,
                        unit: units.find((unit) => String(unit.id) === e.target.value),
                      })
                    }
                  >
                    <option value="">—</option>
                    {units.map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {unit.short_title || unit.title || unit.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    className="offer-field-select"
                    aria-label={t("offers.workspace.inclusion")}
                    value={
                      row.visible_to_customer === false ? "excluded" : row.pricing_role || "included"
                    }
                    onChange={(e) =>
                      update(index, {
                        pricing_role: e.target.value,
                        visible_to_customer: e.target.value !== "excluded",
                      })
                    }
                  >
                    {["included", "optional", "excluded"].map((mode) => (
                      <option key={mode} value={mode}>
                        {t(`offers.workspace.${mode}`)}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="offer-money">{formatMoney(lineAmount(row), currency)}</td>
                <td className="offer-quick-actions">
                  <button
                    type="button"
                    aria-label={t("offers.workspace.duplicate")}
                    onClick={() =>
                      add([
                        {
                          ...row,
                          id: undefined,
                          client_key: undefined,
                          calculation_id: undefined,
                          source_calculation_item_id: undefined,
                          delete: undefined,
                        },
                      ])
                    }
                  >
                    ＋
                  </button>
                  <button
                    type="button"
                    onClick={() => removeRow(index)}
                    aria-label={
                      row.id ? t("offers.workspace.remove_line") : t("offers.workspace.discard_draft")
                    }
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!visibleItems.length ? (
          <div className="offer-workspace-empty">
            <h3>{t("offers.workspace.empty_title")}</h3>
            <p>{t("offers.workspace.empty_hint")}</p>
          </div>
        ) : null}
      </div>

      <footer className="offer-workspace-total">
        <span>{t("offers.workspace.customer_total")}</span>
        <strong>
          {formatMoney(
            items.filter(includedLine).reduce((sum, row) => sum + lineAmount(row), 0),
            currency
          )}
        </strong>
      </footer>
    </div>
  );
}
